import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import PillInput from '../UISystem/Forms/PillInput';
import { SHADOWS } from '../../styles/UISystem';

/**
 * QuickAddOverlay - Name entry for manual contacts
 *
 * Purely presentational — no data persistence. Parent handles all saves.
 * Note, groups, and contact details are added later on the detail page.
 *
 * Props:
 *   onSave({ name }) - called when user taps Add (trimmed name)
 *   onClose - called on backdrop tap (cancel, nothing saved)
 */
const QuickAddOverlay = ({ onSave, onClose }) => {
  // Form state
  const [name, setName] = useState('');

  const nameInputRef = useRef(null);

  // Body scroll lock - use only overflow:hidden to allow iOS keyboard adjustment
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle Add — pass trimmed name to parent
  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim() });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        pointerEvents: 'auto',
      }}
    >
      <motion.div
        initial={{ y: 500 }}
        animate={{ y: 0 }}
        exit={{ y: 500 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxHeight: '80dvh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            padding: '28px 20px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
          }}
        >
          {/* Title */}
          <div
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#000',
              textAlign: 'center',
              marginBottom: '4px',
            }}
          >
            Someone I met
          </div>

          {/* Name input */}
          <PillInput
            ref={nameInputRef}
            variant="shadow"
            name="full-name"
            inputMode="text"
            autoComplete="name"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck="false"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fontSize="20px"
            inputStyle={{
              WebkitBoxShadow: '0 0 0 30px #FFFFFF inset',
              WebkitTextFillColor: name ? '#000' : '#9B9B9B',
              color: name ? '#000' : '#9B9B9B',
            }}
          />

          {/* Add button - always visible, disabled when name is empty */}
          <div style={{ width: '100%', marginTop: '4px' }}>
            <ButtonRounded
              text="Add"
              onClick={handleSave}
              size="full"
              variant="primary"
              disabled={!name.trim()}
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QuickAddOverlay;
